import { ROUTE_NEXTGEN_VIEW } from "~/router/constants";
import { TURN_PARAM, turnAnchorId } from "./components/conversations/useAnchoredScroll";

// Centralized in-app URL builders for nextgen project routes. Keep route
// strings here so a future rename is grep-once instead of grep-and-pray.

export const nextgenProjectsListPath = () => `${ROUTE_NEXTGEN_VIEW}/projects`;

export const nextgenProjectPath = (projectId: string) =>
  `${ROUTE_NEXTGEN_VIEW}/projects/${projectId}`;

export const nextgenProjectChatsPath = (projectId: string) =>
  `${ROUTE_NEXTGEN_VIEW}/projects/${projectId}/c`;

export const nextgenProjectSettingsPath = (projectId: string) =>
  `${ROUTE_NEXTGEN_VIEW}/projects/${projectId}/settings`;

export const nextgenProjectSessionsPath = (projectId: string) =>
  `${ROUTE_NEXTGEN_VIEW}/projects/${projectId}/sessions`;

export const nextgenConversationPath = (projectId: string, conversationId: string) =>
  `${ROUTE_NEXTGEN_VIEW}/projects/${projectId}/c/${conversationId}`;

export const nextgenSharedConversationPath = (token: string) =>
  `${ROUTE_NEXTGEN_VIEW}/shared/c/${token}`;

// A shared-session link's path ends in `/shared/c/<token>`, where the token is
// 26-char Crockford base32 (omits i, l, o, u), matching the backend's
// short_id.SHARE_TOKEN_LENGTH and alphabet. Extraction and validation in one
// pattern mirrors the backend regex.
const SHARE_URL_TOKEN_PATTERN = /\/shared\/c\/([0-9a-hjkmnp-tv-z]{26})$/;

/**
 * Returns the share token iff `rawUrl` is a `/nextgen/shared/c/<token>` link,
 * else null. Matches on the `/shared/c/<token>` tail (rather than the full
 * route prefix) so it stays robust to the nextgen prefix and mirrors the
 * backend validation.
 */
export const parseSharedConversationToken = (rawUrl: string): string | null => {
  let pathname: string;
  try {
    pathname = new URL(rawUrl).pathname;
  } catch {
    return null;
  }
  return pathname.replace(/\/$/, "").match(SHARE_URL_TOKEN_PATTERN)?.[1] ?? null;
};

export const nextgenAdminConversationPath = (conversationId: string) =>
  `${ROUTE_NEXTGEN_VIEW}/admin/c/${conversationId}`;

/**
 * Admin link to a single turn inside a session. A stored turn id can be the full
 * X-Ray header, so it is reduced to the root trace id - the value the
 * conversation view renders as its `data-turn-id` anchor.
 */
export const nextgenAdminTurnPath = (
  conversationId: string,
  turnId: string | undefined
): string => {
  const base = nextgenAdminConversationPath(conversationId);
  const anchor = turnAnchorId(turnId);
  return anchor ? `${base}?${TURN_PARAM}=${encodeURIComponent(anchor)}` : base;
};
